import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../../Colors";
import { StyledTextField } from "../../../components/text-fields/StyledTextField";
import { HelpTooltip } from "../../../subcomponents/HelpTooltip";
import { RootState, useAppDispatch } from "../../../store";
import {
  addProviderGroup,
  updateProviderGroup,
} from "../../../slices/ProviderSlice";
import { ProviderGroup } from "../../../types";
import { dropdownStyles, menuPropsStyles } from "../../../styles/GeneralStyles";

const sectionLabelSx = {
  color: Colors.coolWhite,
  fontSize: "0.813rem",
  fontWeight: 600,
  letterSpacing: "0.2px",
  mb: 1,
};

const containedButtonSx = {
  textTransform: "none" as const,
  fontSize: "0.875rem",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  group?: ProviderGroup;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

export default function ProviderGroupFormModal({
  open,
  mode,
  group,
  onClose,
  onSuccess,
}: Props) {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const providers = useSelector(
    (state: RootState) => state.providers.providers
  );

  const [name, setName] = useState("");
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [primaryProviderIds, setPrimaryProviderIds] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && group) {
      setName(group.name || "");
      setProviderIds(group.providerIds || []);
      setPrimaryProviderIds(group.primaryProviderIds || []);
      setKeywords(Array.isArray(group.keyword) ? group.keyword : group.keyword ? [group.keyword] : []);
    } else {
      setName("");
      setProviderIds([]);
      setPrimaryProviderIds([]);
      setKeywords([]);
    }
    setFormError("");
  }, [open, mode, group]);

  const providerNameByEhrId = useMemo(() => {
    const map = new Map<string, string>();
    providers.forEach((p) => map.set(p.ehrProviderId, p.providerName));
    return map;
  }, [providers]);

  const primaryNotInGroup = useMemo(
    () => primaryProviderIds.filter((id) => !providerIds.includes(id)),
    [primaryProviderIds, providerIds]
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!user?.token) return;
    if (!name.trim()) {
      setFormError("Group name is required.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      if (mode === "add") {
        await dispatch(
          addProviderGroup({
            group: {
              name: name.trim(),
              providerIds,
              primaryProviderIds,
              keyword: keywords,
            },
            token: user.token,
          })
        ).unwrap();
        onSuccess("Provider group created");
      } else if (group) {
        await dispatch(
          updateProviderGroup({
            providerGroupId: group.providerGroupId,
            group: {
              name: name.trim(),
              providerIds,
              primaryProviderIds,
              keyword: keywords,
            },
            token: user.token,
          })
        ).unwrap();
        onSuccess("Provider group updated");
      }
      onClose();
    } catch {
      setFormError(
        mode === "add"
          ? "Failed to create group. Please try again."
          : "Failed to update group. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: Colors.bg2,
          color: "#FFF",
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          pb: 1,
        }}
      >
        {mode === "add" ? "Add Provider Group" : "Edit Provider Group"}
        <IconButton
          onClick={onClose}
          disabled={submitting}
          sx={{ color: Colors.info }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <form onSubmit={handleSubmit} id="provider-group-form">
          {formError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {formError}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              Group Name
            </Typography>
            <StyledTextField value={name} onChange={(value) => setName(value)} />
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              Providers
            </Typography>
            <Select
              multiple
              displayEmpty
              value={providerIds}
              onChange={(e) => setProviderIds(e.target.value as string[])}
              renderValue={(selected) => {
                if ((selected as string[]).length === 0) {
                  return (
                    <Typography
                      sx={{ color: Colors.info, fontSize: "0.875rem" }}
                    >
                      Select providers
                    </Typography>
                  );
                }
                return (selected as string[])
                  .map((value) => providerNameByEhrId.get(value) || value)
                  .join(", ");
              }}
              sx={dropdownStyles}
              MenuProps={menuPropsStyles}
            >
              {providers.map((provider) => (
                <MenuItem
                  key={provider.ehrProviderId}
                  value={provider.ehrProviderId}
                >
                  <Checkbox
                    checked={providerIds.includes(provider.ehrProviderId)}
                    sx={{ color: "#FFF" }}
                  />
                  <ListItemText primary={provider.providerName} />
                </MenuItem>
              ))}
            </Select>
            <Typography
              variant="caption"
              sx={{
                color: Colors.info,
                fontSize: "0.75rem",
                mt: 0.75,
              }}
            >
              {providerIds.length} provider
              {providerIds.length === 1 ? "" : "s"} selected
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mb: 1 }}>
            <Box display="flex" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ ...sectionLabelSx, mb: 0 }}>
                Primary Providers
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 400 }}
              >
                (Advanced)
              </Typography>
              <HelpTooltip
                title={
                  "Auto-assign new patients to this group when one of these providers performed the initial evaluation."
                }
              />
            </Box>
            <Select
              multiple
              displayEmpty
              value={primaryProviderIds}
              onChange={(e) =>
                setPrimaryProviderIds(e.target.value as string[])
              }
              renderValue={(selected) => {
                if ((selected as string[]).length === 0) {
                  return (
                    <Typography
                      sx={{ color: Colors.info, fontSize: "0.875rem" }}
                    >
                      Select primary providers
                    </Typography>
                  );
                }
                return (selected as string[])
                  .map((value) => providerNameByEhrId.get(value) || value)
                  .join(", ");
              }}
              sx={dropdownStyles}
              MenuProps={menuPropsStyles}
            >
              {providers.map((provider) => (
                <MenuItem
                  key={provider.ehrProviderId}
                  value={provider.ehrProviderId}
                >
                  <Checkbox
                    checked={primaryProviderIds.includes(provider.ehrProviderId)}
                    sx={{ color: "#FFF" }}
                  />
                  <ListItemText primary={provider.providerName} />
                </MenuItem>
              ))}
            </Select>
            {primaryNotInGroup.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: Colors.warning,
                  fontSize: "0.75rem",
                  mt: 0.75,
                }}
              >
                Heads up:{" "}
                {primaryNotInGroup
                  .map((id) => providerNameByEhrId.get(id) || id)
                  .join(", ")}{" "}
                {primaryNotInGroup.length === 1 ? "is" : "are"} marked primary
                but not in this group.
              </Typography>
            )}
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mt: 3 }}>
            <Box display="flex" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ ...sectionLabelSx, mb: 0 }}>
                Case Study Keywords
              </Typography>
              <HelpTooltip title="Case-insensitive substrings matched against a patient's case study name to auto-assign them to this group." />
            </Box>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={keywords}
              onChange={(_e, newValue) => setKeywords(newValue as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip {...tagProps} key={key} label={option} size="small" sx={{ fontSize: "0.75rem", height: 20 }} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type and press Enter"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255,255,255,0.05)",
                      color: "#FFF",
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&.Mui-focused fieldset": { borderColor: Colors.primary },
                    },
                    "& .MuiInputBase-input::placeholder": { color: Colors.info },
                  }}
                />
              )}
            />
            <Typography variant="caption" sx={{ color: Colors.info, fontSize: "0.75rem", mt: 0.75 }}>
              Patients whose case study name contains any of these keywords will be auto-assigned to this group.
            </Typography>
          </Box>
        </form>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          p: 2.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", color: Colors.info }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="provider-group-form"
          variant="contained"
          disabled={submitting}
          sx={containedButtonSx}
        >
          {submitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "inherit" }} />
              Saving...
            </Box>
          ) : mode === "add" ? (
            "Create Group"
          ) : (
            "Save Changes"
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
